/**
 * Interface representing RGB color values
 */
interface RGB {
  r: number
  g: number
  b: number
}

/**
 * Convert RGB values to hex color code
 *
 * @param r - Red (0-255)
 * @param g - Green (0-255)
 * @param b - Blue (0-255)
 * @returns Hex color code (e.g., "#FF5733")
 */
export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
}

/**
 * Helper function to convert hex to RGB
 *
 * @param hex - Hex color code
 * @returns Object with r, g, b properties or null if invalid
 */
export function hexToRgb(hex: string): RGB | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    }
    : null
}

// RGB string to hex
export function rgbStringToHex(color: string) {
  const regex = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(\d*\.?\d+))?\s*\)$/;
  const match = color.match(regex);

  if (!match) return null;

  const r = parseInt(match[1]);
  const g = parseInt(match[2]);
  const b = parseInt(match[3]);
  const a = match[4] !== undefined ? parseFloat(match[4]) : null;

  // Validate RGB values
  if ([r, g, b].some(n => n < 0 || n > 255)) return null;

  // Validate Alpha
  if (a !== null && (a < 0 || a > 1)) return null;

  const toHex = (n: number) => n.toString(16).padStart(2, '0');

  const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;

  // If alpha is defined, add AA to the hex
  if (a !== null) {
    const alphaHex = toHex(Math.round(a * 255));
    return hex + alphaHex;
  }

  return hex;
}

export function rgbToHsl({ r, g, b }: RGB): [number, number, number] {
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0,
    s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  return [h, s, l]
}

// Convert HSL to RGB
export function hslToRgb(h: number, s: number, l: number): RGB {
  let r: number, g: number, b: number

  if (s === 0) {
    r = g = b = l
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q

    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  }
}

// Final function: generate floor color
export function generateFloorColor(bgHex: string): string {
  if (bgHex.includes('rgb')) {
    const hex = rgbStringToHex(bgHex)
    if (hex) {
      bgHex = hex
    }
  }
  const rgb = hexToRgb(bgHex)
  if (!rgb) throw new Error('Invalid HEX input')

  const [h, s, l] = rgbToHsl(rgb)

  let newL: number
  if (l >= 0.85) {
    newL = 0.7
  } else if (l < 0.5) {
    newL = 0.8
  } else if (l < 0.6) {
    newL = 0.85
  } else if (l < 0.7) {
    newL = 0.9
  } else {
    newL = 0.93
  }

  const newRgb = hslToRgb(h, s, newL)
  return rgbToHex(newRgb.r, newRgb.g, newRgb.b)
}

/**
 * Extract top 3 prominent colors from an image using k-means clustering
 */
export async function getProminentColors(imageUrl: string, colorCount = 3): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = imageUrl

    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d', { willReadFrequently: true })!
      const width = (canvas.width = img.width)
      const height = (canvas.height = img.height)
      ctx.drawImage(img, 0, 0, width, height)

      const imageData = ctx.getImageData(0, 0, width, height).data

      // Collect pixels
      const pixels: number[][] = []
      for (let i = 0; i < imageData.length; i += 4) {
        const r = imageData[i]
        const g = imageData[i + 1]
        const b = imageData[i + 2]
        const a = imageData[i + 3]
        if (a > 0) {
          pixels.push([r, g, b])
        }
      }

      if (pixels.length === 0) {
        reject('No pixels found')
        return
      }

      // --- Simple K-means clustering ---
      function euclidean(a: number[], b: number[]) {
        return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2)
      }

      // Randomly pick initial centroids
      const centroids = pixels.sort(() => Math.random() - 0.5).slice(0, colorCount)

      let assignments: number[] = []
      let moved = true
      let iterations = 0

      while (moved && iterations < 20) {
        moved = false
        iterations++

        // Assign pixels to closest centroid
        assignments = pixels.map((p) => {
          let minDist = Infinity
          let idx = 0
          for (let c = 0; c < centroids.length; c++) {
            const dist = euclidean(p, centroids[c])
            if (dist < minDist) {
              minDist = dist
              idx = c
            }
          }
          return idx
        })

        // Update centroids
        for (let c = 0; c < centroids.length; c++) {
          const cluster = pixels.filter((_, i) => assignments[i] === c)
          if (cluster.length > 0) {
            const mean = cluster.reduce(
              (acc, val) => [acc[0] + val[0], acc[1] + val[1], acc[2] + val[2]],
              [0, 0, 0],
            )
            const newCentroid = mean.map((v) => Math.round(v / cluster.length))
            if (euclidean(newCentroid, centroids[c]) > 1) {
              centroids[c] = newCentroid as number[]
              moved = true
            }
          }
        }
      }

      // Count cluster sizes
      const counts = new Array(colorCount).fill(0)
      assignments.forEach((idx) => counts[idx]++)

      // Sort centroids by cluster size
      const sorted = centroids
        .map((c, i) => ({ color: c, count: counts[i] }))
        .sort((a, b) => b.count - a.count)

      // Return top N as rgb strings
      resolve(sorted.slice(0, colorCount).map((c) => `rgb(${c.color.join(',')})`))
    }

    img.onerror = reject
  })
}

export function parseRgbString(rgbStr: string) {
  const match = rgbStr.replace(/\s+/g, '').match(/^rgb?\((\d+),(\d+),(\d+)(?:,([\d.]+))?\)$/i)
  if (!match) return null
  const [, r, g, b] = match
  return {
    r: Number(r),
    g: Number(g),
    b: Number(b),
  }
}
